import { assertTokenValidForMutation, handleResponse } from './service-helpers'
import log from '@/lib/logger'

// Types - simple interfaces matching backend DTOs
export interface BillingRunSummary {
  id: string
  totalAmount: number
  memberCount: number
  createdAt: string
  membershipFeeCount: number
  oneTimeDonationCount: number
  annualDonationCount: number
}

export interface BillingRun {
  id: string
  organizationId: string
  totalAmount: number
  memberCount: number
  createdAt: string
  createdBy: string
  sepaXmlGeneratedAt?: string
  membershipFeeCount: number
  oneTimeDonationCount: number
  annualDonationCount: number
}

export interface BillingRunDeleteCheck {
  hasPaidItems: boolean
  hasSepaXml: boolean
}

export interface BillingRunItem {
  id: string
  memberId: string
  memberName: string
  memberNumber: string
  feeTypeName: string
  amount: number
  periodStart: string
  periodEnd: string
  paymentMethod: string
  paymentStatus?: string
  paidAt?: string
  paymentUpdatedBy?: string
  itemType: string
  cancellationReason?: string
}

export interface UpdatePaymentStatusDto {
  paymentStatus: string
  cancellationReason?: string
}

export interface BulkUpdatePaymentStatusDto {
  itemIds: string[]
  paymentStatus: string
}

export interface SepaStatus {
  available: boolean
  reason: string | null
}

// API Functions

export async function getSepaStatus(token: string): Promise<SepaStatus> {
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/sepa/status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    return await handleResponse<SepaStatus>(response) ?? { available: false, reason: 'UNKNOWN' }
  } catch {
    return { available: false, reason: 'UNKNOWN' }
  }
}
export async function createBillingRun(token: string): Promise<BillingRun | null> {
  assertTokenValidForMutation()
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    const result = await handleResponse<BillingRun>(response)
    return result || null
  } catch (error) {
    log.error('Error creating billing run:', error)
    throw error
  }
}

export async function getBillingRuns(token: string): Promise<BillingRunSummary[]> {
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    const data = await handleResponse<BillingRunSummary[]>(response)
    return data || []
  } catch (error) {
    log.error('Error fetching billing runs:', error)
    return []
  }
}

export async function getBillingRunById(token: string, id: string): Promise<BillingRun | null> {
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    const result = await handleResponse<BillingRun>(response)
    return result || null
  } catch (error) {
    log.error('Error fetching billing run details:', error)
    return null
  }
}

export async function getBillingRunItems(token: string, id: string): Promise<BillingRunItem[]> {
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs/${id}/items`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    const data = await handleResponse<BillingRunItem[]>(response)
    return data || []
  } catch (error) {
    log.error('Error fetching billing run items:', error)
    return []
  }
}

export async function updateBillingItemPaymentStatus(
  token: string, 
  itemId: string, 
  updateDto: UpdatePaymentStatusDto
): Promise<BillingRunItem | null> {
  assertTokenValidForMutation()
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/items/${itemId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateDto)
    })
    
    const result = await handleResponse<BillingRunItem>(response)
    return result || null
  } catch (error) {
    log.error('Error updating payment status:', error)
    throw error
  }
}

export async function bulkUpdateBillingItemsPaymentStatus(
  token: string, 
  bulkUpdateDto: BulkUpdatePaymentStatusDto
): Promise<BillingRunItem[]> {
  assertTokenValidForMutation()
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/items/bulk`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bulkUpdateDto)
    })
    
    const result = await handleResponse<BillingRunItem[]>(response)
    return result || []
  } catch (error) {
    log.error('Error bulk updating payment status:', error)
    throw error
  }
}

export async function deleteBillingRun(token: string, id: string): Promise<boolean> {
  assertTokenValidForMutation()
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    return response.status === 204
  } catch (error) {
    log.error('Error deleting billing run:', error)
    throw error
  }
}

export async function getBillingRunDeleteCheck(token: string, id: string): Promise<BillingRunDeleteCheck | null> {
  try {
    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs/${id}/delete-check`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    const result = await handleResponse<BillingRunDeleteCheck>(response)
    return result || null
  } catch (error) {
    log.error('Error fetching delete check:', error)
    return null
  }
}

export async function downloadSepaXml(
  token: string,
  billingRunId: string,
  executionDate: string
): Promise<Blob> {
  assertTokenValidForMutation()
  const response = await fetch(
    `${import.meta.env.VITE_NBH_URL_ROOT}/api/finance/billing/runs/${billingRunId}/sepa-xml`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ executionDate })
    }
  )

  if (!response.ok) {
    if (response.status === 422) {
      throw new Error('SEPA_CONFIG_MISSING')
    }
    throw new Error(`HTTP ${response.status}`)
  }

  return response.blob()
}